import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { MoreVertical, FileText, Clock, PlayCircle, Loader2, Download, Trash2, FileX, ChevronLeft, ChevronRight, Check, Folder } from 'lucide-react';
import { Button, Card, Badge, ConfirmDialog, Modal } from '../components/UIComponents';
import { useNavigate, useLocation } from 'react-router-dom';
import History from './History';
import { useTransition } from '../hooks/useTransition';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../components/ThemeProvider';
import { useSubscription, useQuota, useUsageActivity } from '../hooks/useBilling';
import { useTranscriptions } from '../hooks/useTranscriptions';
import { deleteOriginalFile } from '../api/files';
import * as transcriptionsApi from '../api/transcriptions';
import EditableFilename from '../components/EditableFilename';
import ExportTranscript, { type ExportOptions } from '../components/ExportTranscript';
import GoYearlyOfferBanner from '../components/GoYearlyOfferBanner';
import { buildExportFilename, downloadBlob, generateJSON, generateMarkdown, generateSRT, generateTXT, generateVTT } from '../utils/exportUtils';
import { stripKnownTranscriptionTitleExtension } from '../utils/transcriptionTitle';
import { useTranslation } from 'react-i18next';

// Map transcription status to display badge
const getTranscriptionStatusBadge = (status: transcriptionsApi.TranscriptionStatus, t: (key: string) => string) => {
  switch (status) {
    case 'completed': return <Badge variant="success">{t('transcription.status.completed')}</Badge>;
    case 'processing': return <Badge variant="info">{t('transcription.status.processing')}</Badge>;
    case 'failed': return <Badge variant="warning">{t('transcription.status.failed')}</Badge>;
    case 'queued': return <Badge variant="default">{t('transcription.status.queued')}</Badge>;
    case 'cancelled': return <Badge variant="default">{t('transcription.status.cancelled')}</Badge>;
    default: return <Badge>{t('transcription.status.queued')}</Badge>;
  }
};

// Format duration from seconds to MM:SS
const formatDuration = (seconds: number): string => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Format date with time
const formatDateTime = (dateString: string, locale: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// Action Menu Dropdown component
interface ActionMenuProps {
  transcription: transcriptionsApi.TranscriptionResponse;
  onExport: (transcription: transcriptionsApi.TranscriptionResponse) => void;
  onMove: (transcription: transcriptionsApi.TranscriptionResponse) => void;
  onDeleteOriginal: (transcription: transcriptionsApi.TranscriptionResponse) => void;
  onDeleteTranscription: (transcription: transcriptionsApi.TranscriptionResponse) => void;
  t: (key: string) => string;
}

const ActionMenu: React.FC<ActionMenuProps> = ({ transcription, onExport, onMove, onDeleteOriginal, onDeleteTranscription, t }) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; openUp: boolean } | null>(null);
  const { shouldMount, isVisible } = useTransition(isOpen, 200);

  const updateMenuPosition = useCallback((menuHeight?: number) => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;

    const menuWidth = 240; // w-60
    const height = menuHeight ?? 232;
    const margin = 4;
    const padding = 8;
    const shouldOpenUp = rect.bottom + height + margin > window.innerHeight - padding;
    const top = shouldOpenUp
      ? Math.max(padding, rect.top - height - margin)
      : rect.bottom + margin;
    const left = Math.max(padding, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - padding));
    setMenuPosition({ top, left, openUp: shouldOpenUp });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current && menuRef.current.contains(target)) return;
      if (buttonRef.current && buttonRef.current.contains(target)) return;
      setIsOpen(false);
    };

    const handleWindowChanged = () => {
      if (!isOpen) return;
      updateMenuPosition(menuRef.current?.offsetHeight);
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleWindowChanged);
    window.addEventListener('scroll', handleWindowChanged, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleWindowChanged);
      window.removeEventListener('scroll', handleWindowChanged, true);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return;
    updateMenuPosition(menuRef.current?.offsetHeight);
  }, [isOpen, updateMenuPosition]);

  const handleAction = (action: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    action();
  };

  const canExport = transcription.status === 'completed';
  const canDeleteOriginal = transcription.status === 'completed' && transcription.file_status !== 'transcription_only';

  return (
    <div className="relative">
      <button
        type="button"
        aria-label={t('common.moreActions')}
        className="text-slate-400 hover:text-slate-600 dark:text-zinc-500 dark:hover:text-zinc-300 p-2 rounded-md hover:bg-slate-100 dark:hover:bg-zinc-700/60 transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => {
            const next = !prev;
            if (next) {
              updateMenuPosition();
            }
            return next;
          });
        }}
        ref={buttonRef}
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {shouldMount && menuPosition && createPortal((
        <div
          ref={menuRef}
          className={`fixed w-60 bg-white dark:bg-zinc-800 rounded-lg shadow-lg border border-slate-200 dark:border-zinc-700 py-1 z-[9999] whitespace-nowrap transform transition duration-150 ease-out ${
            isVisible ? 'dropdown-in' : 'dropdown-out'
          } ${menuPosition.openUp ? 'origin-bottom-right' : 'origin-top-right'}`}
          style={{ top: menuPosition.top, left: menuPosition.left }}
        >
          <button
            className="w-full px-4 py-2 text-start text-sm flex items-center gap-2 text-slate-700 dark:text-zinc-200 hover:bg-slate-100 dark:hover:bg-zinc-700 whitespace-nowrap"
            onClick={handleAction(() => onMove(transcription))}
          >
            <Folder className="h-4 w-4" />
            {t('common.move')}
          </button>
          <div className="border-t border-slate-100 dark:border-zinc-700 my-1" />
          <button
            className={`w-full px-4 py-2 text-start text-sm flex items-center gap-2 whitespace-nowrap ${
              canExport
                ? 'text-slate-700 dark:text-zinc-200 hover:bg-slate-100 dark:hover:bg-zinc-700'
                : 'text-slate-400 dark:text-zinc-500 cursor-not-allowed'
            }`}
            onClick={canExport ? handleAction(() => onExport(transcription)) : undefined}
            disabled={!canExport}
          >
            <Download className="h-4 w-4" />
            {t('common.export')}
          </button>
          <button
            className={`w-full px-4 py-2 text-start text-sm flex items-center gap-2 whitespace-nowrap ${
              canDeleteOriginal
                ? 'text-amber-600 dark:text-amber-400 hover:bg-slate-100 dark:hover:bg-zinc-700'
                : 'text-slate-400 dark:text-zinc-500 cursor-not-allowed'
            }`}
            onClick={canDeleteOriginal ? handleAction(() => onDeleteOriginal(transcription)) : undefined}
            disabled={!canDeleteOriginal}
          >
            <FileX className="h-4 w-4" />
            {t('dashboard.deleteOriginalFile')}
          </button>
          <div className="border-t border-slate-100 dark:border-zinc-700 my-1" />
          <button
            className="w-full px-4 py-2 text-start text-sm text-red-600 dark:text-red-400 hover:bg-slate-100 dark:hover:bg-zinc-700 flex items-center gap-2 whitespace-nowrap"
            onClick={handleAction(() => onDeleteTranscription(transcription))}
          >
            <Trash2 className="h-4 w-4" />
            {t('dashboard.deleteTranscription')}
          </button>
        </div>
      ), document.body)}
    </div>
  );
};

interface ToastProps {
  message: string;
  isVisible: boolean;
}

const Toast: React.FC<ToastProps> = ({ message, isVisible: propIsVisible }) => {
  const { shouldMount, isVisible } = useTransition(propIsVisible, 300);
  
  if (!shouldMount) return null;

  return (
    <div className={`fixed bottom-24 left-1/2 -translate-x-1/2 z-50 ${isVisible ? 'toast-in' : 'toast-out'}`}>
      <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-lg shadow-lg">
        <Check className="h-4 w-4 text-green-400" />
        <span className="text-sm font-medium">{message}</span>
      </div>
    </div>
  );
};

interface DashboardOverviewProps {
}

const DashboardOverview: React.FC<DashboardOverviewProps> = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const isDarkMode = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  
  const { data: quota } = useQuota();
  const { data: subscription, loading: subscriptionLoading, startCheckout } = useSubscription();
  const { data: activityData } = useUsageActivity();
  const {
    transcriptions,
    totalTranscriptions,
    totalPages,
    isLoading,
    error: transcriptionsError,
    fetchTranscriptions,
    refreshTranscriptions,
    deleteTranscription,
    getTranscriptionResult
  } = useTranscriptions();

  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [renameOverrides, setRenameOverrides] = useState<Record<string, string>>({});
  const [renamingTranscriptionId, setRenamingTranscriptionId] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<{
    isOpen: boolean;
    type: 'delete_original' | 'delete_transcription';
    transcription: transcriptionsApi.TranscriptionResponse;
  } | null>(null);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [exportTranscription, setExportTranscription] = useState<transcriptionsApi.TranscriptionResponse | null>(null);
  const [exportSegments, setExportSegments] = useState<transcriptionsApi.TranscriptionSegment[]>([]);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [offerCheckoutLoading, setOfferCheckoutLoading] = useState(false);
  const [offerCheckoutError, setOfferCheckoutError] = useState<string | null>(null);
  const [moveTarget, setMoveTarget] = useState<transcriptionsApi.TranscriptionResponse | null>(null);
  const [folders, setFolders] = useState<transcriptionsApi.TranscriptionFolder[]>([]);
  const [isFoldersLoading, setIsFoldersLoading] = useState(false);

  const folderNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const folder of folders) {
      map[folder.id] = folder.name;
    }
    return map;
  }, [folders]);

  // Transform activity data for chart (convert date to day name)
  const locale = i18n.resolvedLanguage || i18n.language;

  const usageData = (activityData || []).map((item) => {
    const date = new Date(item.date);
    const dayName = date.toLocaleDateString(locale, { weekday: 'short' });
    return { name: dayName, minutes: Math.round(item.minutes * 10) / 10 };
  });

  // Load transcriptions
  const loadTranscriptions = useCallback(() => {
    fetchTranscriptions({
      page: currentPage,
      page_size: 10,
    });
  }, [fetchTranscriptions, currentPage]);

  useEffect(() => {
    loadTranscriptions();
  }, [loadTranscriptions]);

  // Handle transcription click - navigate to detail page
  const handleTranscriptionClick = (transcription: transcriptionsApi.TranscriptionResponse) => {
    navigate(`/transcription/${transcription.id}`);
  };

  const showToastNotification = useCallback((message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  }, []);

  const notifyFoldersChanged = useCallback(() => {
    window.dispatchEvent(new Event('transcription-folders:changed'));
  }, []);

  const loadFolders = useCallback(async () => {
    setIsFoldersLoading(true);
    try {
      const resp = await transcriptionsApi.listTranscriptionFolders();
      setFolders(resp.folders || []);
    } catch (err) {
      console.error(err);
      showToastNotification(t('dashboard.toast.failedToLoadFolders'));
    } finally {
      setIsFoldersLoading(false);
    }
  }, [showToastNotification]);

  useEffect(() => {
    void loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    const onChanged = () => {
      void loadFolders();
    };
    window.addEventListener('transcription-folders:changed', onChanged);
    return () => {
      window.removeEventListener('transcription-folders:changed', onChanged);
    };
  }, [loadFolders]);

  useEffect(() => {
    const onChanged = () => {
      void refreshTranscriptions();
    };
    window.addEventListener('transcriptions:changed', onChanged);
    return () => {
      window.removeEventListener('transcriptions:changed', onChanged);
    };
  }, [refreshTranscriptions]);

  const openMoveModal = useCallback(async (t: transcriptionsApi.TranscriptionResponse) => {
    setMoveTarget(t);
    if (folders.length === 0) {
      await loadFolders();
    }
  }, [folders.length, loadFolders]);

  const handleMoveToFolder = useCallback(async (folderId: string | null) => {
    if (!moveTarget) return;
    try {
      await transcriptionsApi.moveTranscription(moveTarget.id, folderId);
      setMoveTarget(null);
      await refreshTranscriptions();
      notifyFoldersChanged();
      showToastNotification(t('dashboard.toast.moved'));
    } catch (err) {
      console.error(err);
      showToastNotification(t('dashboard.toast.failedToMove'));
    }
  }, [moveTarget, notifyFoldersChanged, refreshTranscriptions, showToastNotification]);

  const closeExportModal = () => {
    setIsExportOpen(false);
    setExportTranscription(null);
    setExportSegments([]);
    setIsExporting(false);
    setIsExportLoading(false);
  };

  // Handle Export action
  const handleExport = async (transcription: transcriptionsApi.TranscriptionResponse) => {
    setError(null);
    setExportTranscription(transcription);
    setExportSegments([]);
    setIsExportOpen(true);
    setIsExportLoading(true);

    try {
      const result = await getTranscriptionResult(transcription.id);
      setExportSegments(result.segments || []);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('dashboard.toast.failedToLoadTranscript');
        setError(message);
        showToastNotification(message);
      } finally {
      setIsExportLoading(false);
    }
  };

  // Handle Delete Original File Click
  const handleDeleteOriginalClick = (transcription: transcriptionsApi.TranscriptionResponse) => {
    setConfirmation({
      isOpen: true,
      type: 'delete_original',
      transcription
    });
  };

  // Handle Delete Transcription Click
  const handleDeleteTranscriptionClick = (transcription: transcriptionsApi.TranscriptionResponse) => {
    setConfirmation({
      isOpen: true,
      type: 'delete_transcription',
      transcription
    });
  };

  const handleConfirmAction = async () => {
    if (!confirmation) return;
    const { type, transcription } = confirmation;

    setActionLoading(transcription.id);
    setError(null);
    
    try {
      if (type === 'delete_original') {
        await deleteOriginalFile(transcription.file_id);
        loadTranscriptions();
      } else {
        await deleteTranscription(transcription.id);
      }
      setConfirmation(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dashboard.errors.actionFailed'));
      setConfirmation(null);
    } finally {
      setActionLoading(null);
    }
  };

  const usedMinutes = Math.round(quota?.used_minutes || 0);
  const monthlyMinutes = quota?.monthly_minutes || 120;
  const bonusMinutes = quota?.bonus_minutes || 0;
  const totalMinutes = monthlyMinutes + bonusMinutes;
  const usagePercentage = totalMinutes > 0 ? Math.round((usedMinutes / totalMinutes) * 100) : 0;

  // Daily uploads data
  const dailyFilesUsed = quota?.daily_files_used || 0;
  const dailyFilesLimit = quota?.daily_files_limit ?? -1; // -1 means unlimited
  const isUnlimitedDailyUploads = dailyFilesLimit === -1;
  const dailyUploadsPercentage = isUnlimitedDailyUploads ? 0 : (dailyFilesLimit > 0 ? Math.round((dailyFilesUsed / dailyFilesLimit) * 100) : 0);
  const dailyUploadsRemaining = isUnlimitedDailyUploads ? Infinity : Math.max(0, dailyFilesLimit - dailyFilesUsed);

  // Get tier name from subscription
  const planName = subscription?.plan?.name || subscription?.plan_id;
  const tierName = planName
    ? t('dashboard.tier.planEdition', { plan: planName.charAt(0).toUpperCase() + planName.slice(1) })
    : t('dashboard.tier.freeEdition');

  const displayError = error || transcriptionsError;
  const hasPaidSubscription = subscription?.status === 'active' && subscription.plan_id !== 'free';

  const handleGoOfferCheckout = useCallback(async () => {
    setOfferCheckoutLoading(true);
    setOfferCheckoutError(null);
    try {
      await startCheckout('go', 'yearly', 'GO59');
    } catch (err) {
      setOfferCheckoutError(err instanceof Error ? err.message : t('dashboard.errors.actionFailed'));
      setOfferCheckoutLoading(false);
    }
  }, [startCheckout, t]);

  const handleRename = useCallback(
    async (transcription: transcriptionsApi.TranscriptionResponse, nextName: string, currentName: string) => {
      setError(null);
      setRenameOverrides((prev) => ({ ...prev, [transcription.id]: nextName }));
      setRenamingTranscriptionId(transcription.id);

      try {
        const updatedTranscription = await transcriptionsApi.updateTranscriptionDisplayName(transcription.id, nextName);
        setRenameOverrides((prev) => ({
          ...prev,
          [transcription.id]: updatedTranscription.display_name ?? nextName
        }));
      } catch (err) {
        setRenameOverrides((prev) => ({ ...prev, [transcription.id]: currentName }));
        setError(err instanceof Error ? err.message : t('dashboard.errors.failedToRenameTranscription'));
        throw err;
      } finally {
        setRenamingTranscriptionId((prev) => (prev === transcription.id ? null : prev));
      }
    },
    []
  );

  const handleExportSubmit = (format: string, options: ExportOptions) => {
    if (!exportTranscription) {
      showToastNotification(t('dashboard.toast.noTranscriptionSelected'));
      return;
    }
    if (isExportLoading) {
      showToastNotification(t('dashboard.toast.transcriptStillLoading'));
      return;
    }

    let content = '';
    let mimeType = 'text/plain';
    let extension = format;

    setIsExporting(true);

    try {
      switch (format) {
        case 'txt':
          content = generateTXT(exportSegments, options);
          mimeType = 'text/plain';
          break;
        case 'json':
          content = generateJSON(exportSegments);
          mimeType = 'application/json';
          break;
        case 'md':
          content = generateMarkdown(exportSegments, options);
          mimeType = 'text/markdown';
          break;
        case 'srt':
          content = generateSRT(exportSegments);
          mimeType = 'text/plain';
          break;
        case 'vtt':
          content = generateVTT(exportSegments);
          mimeType = 'text/vtt';
          break;
          case 'docx':
          case 'pdf':
            showToastNotification(t('dashboard.toast.exportComingSoon', { format: format.toUpperCase() }));
            closeExportModal();
            return;
          default:
            showToastNotification(t('dashboard.toast.unsupportedExportFormat'));
            return;
      }

      const exportDisplayName = renameOverrides[exportTranscription.id]
        ?? exportTranscription.display_name
        ?? exportTranscription.file_name
        ?? exportTranscription.file_original_name
        ?? t('transcription.fallbackTitle', { id: exportTranscription.id.slice(-6) });
      const filename = buildExportFilename(
        exportDisplayName,
        extension,
        exportTranscription.display_name_user_set !== true
      );
      downloadBlob(content, filename, mimeType);
      showToastNotification(t('dashboard.toast.exportedAs', { format: format.toUpperCase() }));
      closeExportModal();
    } catch (err) {
      console.error('Export failed:', err);
      showToastNotification(t('dashboard.toast.failedToExportTranscript'));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      {/* Welcome & Action */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="h-page">
            {t('dashboard.welcome')}, {user?.name?.split(' ')[0] || t('common.user')}
          </h1>
          <p className="t-small mt-1">{t('dashboard.subtitle')}</p>
        </div>
      </div>

      {/* Go yearly offer - show only when the user has no paid subscription */}
      {!subscriptionLoading && !hasPaidSubscription && (
        <GoYearlyOfferBanner
          loading={offerCheckoutLoading}
          error={offerCheckoutError}
          onUpgrade={handleGoOfferCheckout}
          onSeePlans={() => navigate('/pricing')}
        />
      )}

      {displayError && (
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {displayError}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6 col-span-1 md:col-span-2">
          <div className="flex items-center justify-between mb-4">
             <h3 className="font-semibold text-slate-900 dark:text-zinc-100">{t('dashboard.usageActivity')}</h3>
             <span className="text-xs font-medium text-slate-400 dark:text-zinc-500">{t('dashboard.last7Days')}</span>
          </div>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={usageData}>
                <CartesianGrid vertical={false} stroke={isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'} />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: isDarkMode ? '#7c7c82' : '#8e8e93', fontSize: 12}} dy={8} />
                <YAxis axisLine={false} tickLine={false} width={32} tick={{fill: isDarkMode ? '#7c7c82' : '#8e8e93', fontSize: 12}} />
                <Tooltip 
                  cursor={{fill: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', radius: 6} as never} 
                  contentStyle={{
                    borderRadius: '12px', 
                    border: 'none', 
                    boxShadow: isDarkMode ? '0 12px 32px -8px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.08)' : '0 12px 32px -8px rgba(15,30,70,0.18), 0 0 0 1px rgba(0,0,0,0.05)',
                    backgroundColor: isDarkMode ? '#1f1f22' : '#ffffff',
                    color: isDarkMode ? '#f5f5f7' : '#1d1d1f',
                    fontSize: 13,
                  }} 
                />
                <Bar dataKey="minutes" fill={isDarkMode ? '#0a84ff' : '#007aff'} radius={[5, 5, 5, 5]} maxBarSize={22} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 flex flex-col justify-between">
           <div>
             <div className="flex items-center justify-between">
               <h3 className="font-semibold text-ink">{t('dashboard.monthlyPlan')}</h3>
               <span className="chip">{tierName}</span>
             </div>
           </div>

           <div className="my-5">
             <div className="flex items-baseline gap-1.5 mb-3">
               <span className="figure text-[44px] text-ink">{usedMinutes}</span>
                <span className="text-ink-2 text-[15px] font-medium">/ {totalMinutes.toLocaleString()} {t('common.minutesShort')}</span>
             </div>
             <div className="w-full bg-card-hi rounded-full h-1.5 overflow-hidden">
               <div className={`h-1.5 rounded-full transition-all duration-500 ${usagePercentage >= 90 ? 'bg-[#ff9f0a]' : 'bg-accent'}`} style={{ width: `${Math.min(usagePercentage, 100)}%` }}></div>
             </div>
              <p className="text-ink-3 text-[12.5px] mt-2">
                {t('dashboard.usagePercent', { percent: usagePercentage })}
                {bonusMinutes > 0 && ` ${t('dashboard.includesBonus', { minutes: bonusMinutes })}`}
              </p>
           </div>

           {/* Daily Uploads Section */}
           <div className="mb-5 pt-4 border-t border-line">
             <div className="flex items-center justify-between mb-2">
               <span className="text-ink text-[14px] font-medium">{t('dashboard.dailyUploads')}</span>
                <span className="tnum text-ink-2 text-[14px]">
                  {isUnlimitedDailyUploads ? (
                    t('dashboard.unlimited')
                  ) : (
                    <>{dailyFilesUsed} / {dailyFilesLimit}</>
                  )}
                </span>
             </div>
             {!isUnlimitedDailyUploads && (
               <>
                 <div className="w-full bg-card-hi rounded-full h-1.5 overflow-hidden">
                   <div
                     className={`h-1.5 rounded-full transition-all duration-500 ${dailyUploadsPercentage >= 100 ? 'bg-[#ff3b30]' : 'bg-accent'}`}
                     style={{ width: `${Math.min(dailyUploadsPercentage, 100)}%` }}
                   ></div>
                 </div>
                  <p className="text-ink-3 text-[12.5px] mt-2">
                    {dailyUploadsRemaining > 0
                      ? t('dashboard.uploadsRemaining', { count: dailyUploadsRemaining })
                      : t('dashboard.limitReached')}
                  </p>
               </>
             )}
           </div>

           <button
              className="btn btn-sm w-full !py-2.5"
              onClick={() => navigate('/pricing')}
           >
              {t('dashboard.upgradePlan')}
           </button>
        </Card>
      </div>

      {/* All Transcriptions Table */}
      <Card>
        <div className="p-6 border-b border-slate-100 dark:border-zinc-800 flex items-center justify-between">
           <h3 className="font-semibold text-slate-900 dark:text-zinc-100">{t('dashboard.allTranscriptions')}</h3>
           <span className="text-sm text-slate-500 dark:text-zinc-400">{totalTranscriptions} {t('common.total')}</span>
        </div>
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-8 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
           ) : transcriptions.length === 0 ? (
            <div className="p-8 text-center text-slate-500 dark:text-zinc-400">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('dashboard.noTranscriptions')}</p>
              <p className="text-sm text-slate-500 dark:text-zinc-500 mt-3">
                {t('dashboard.uploadHint')}
              </p>
            </div>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-line">
                  <th className="px-6 py-3 text-[12.5px] font-medium text-ink-3">{t('dashboard.table.transcription')}</th>
                  <th className="px-6 py-3 text-[12.5px] font-medium text-ink-3">{t('dashboard.table.folder')}</th>
                  <th className="px-6 py-3 text-[12.5px] font-medium text-ink-3">{t('dashboard.table.duration')}</th>
                  <th className="px-6 py-3 text-[12.5px] font-medium text-ink-3">{t('dashboard.table.date')}</th>
                  <th className="px-6 py-3 text-[12.5px] font-medium text-ink-3">{t('dashboard.table.status')}</th>
                  <th className="px-6 py-3 text-[12.5px] font-medium text-ink-3 text-end">{t('dashboard.table.action')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-zinc-800">
                {transcriptions.map((transcription) => {
                  const overrideName = renameOverrides[transcription.id];
                  const rawDisplayName = overrideName
                    ?? transcription.display_name
                    ?? transcription.file_name
                    ?? transcription.file_original_name
                    ?? t('common.untitled');
                  const shouldStripExtension = !overrideName && transcription.display_name_user_set !== true;
                  const displayName = (shouldStripExtension
                    ? stripKnownTranscriptionTitleExtension(rawDisplayName)
                    : rawDisplayName.trim()) || t('common.untitled');

                  return (
                  <tr
                    key={transcription.id}
                    className={`hover:bg-slate-50 dark:hover:bg-zinc-800/50 transition-colors cursor-pointer ${actionLoading === transcription.id ? 'opacity-50' : ''}`}
                    onClick={() => handleTranscriptionClick(transcription)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                          <PlayCircle className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <EditableFilename
                              value={displayName}
                              maxLength={255}
                              isLoading={renamingTranscriptionId === transcription.id}
                              onSave={(nextName) => handleRename(transcription, nextName, displayName)}
                            />
                          </div>
                          {transcription.content_preview && (
                            <p className="text-xs text-slate-500 dark:text-zinc-400 line-clamp-1 max-w-xs">
                              {transcription.content_preview}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {(() => {
                        const folderName = transcription.folder_id ? (folderNameById[transcription.folder_id] ?? t('common.uncategorized')) : t('common.uncategorized');
                        return (
                          <Badge variant="neutral" className="max-w-[120px] truncate" title={folderName}>
                            {folderName}
                          </Badge>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 dark:text-zinc-400">
                       <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {formatDuration(transcription.duration || 0)}
                       </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 dark:text-zinc-400">{formatDateTime(transcription.created_at, locale)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getTranscriptionStatusBadge(transcription.status, t)}
                        {transcription.file_status === 'transcription_only' && (
                          <Badge variant="default">{t('dashboard.textOnly')}</Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-end">
                      <ActionMenu
                        transcription={transcription}
                        onExport={handleExport}
                        onMove={openMoveModal}
                        onDeleteOriginal={handleDeleteOriginalClick}
                        onDeleteTranscription={handleDeleteTranscriptionClick}
                        t={t}
                      />
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-slate-100 dark:border-zinc-800 flex items-center justify-between">
            <div className="text-sm text-slate-500 dark:text-zinc-400">
              {t('dashboard.showingResults', { 
                from: Math.min(totalTranscriptions, (currentPage - 1) * 10 + 1),
                to: Math.min(totalTranscriptions, currentPage * 10),
                total: totalTranscriptions
              })}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1 || isLoading}
                onClick={() => setCurrentPage(p => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages || isLoading}
                onClick={() => setCurrentPage(p => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Modal isOpen={isExportOpen} onClose={closeExportModal} title={t('transcription.export.title')}>
        <ExportTranscript
          onExport={handleExportSubmit}
          onCancel={closeExportModal}
          isExporting={isExporting || isExportLoading}
        />
      </Modal>

      <Modal isOpen={!!moveTarget} onClose={() => setMoveTarget(null)} title={t('dashboard.moveToFolder')}>
        {isFoldersLoading ? (
          <div className="text-sm text-slate-500 dark:text-zinc-400">{t('common.loading')}</div>
        ) : (
          <div className="space-y-2">
            <button
              className="w-full text-start px-3 py-2 rounded-lg text-sm hover:bg-slate-50 dark:hover:bg-zinc-800"
              onClick={() => handleMoveToFolder(null)}
            >
              {t('common.uncategorized')}
            </button>
            {folders.map((f) => (
              <button
                key={f.id}
                className="w-full text-start px-3 py-2 rounded-lg text-sm hover:bg-slate-50 dark:hover:bg-zinc-800"
                onClick={() => handleMoveToFolder(f.id)}
              >
                {f.name}
              </button>
            ))}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!confirmation}
        onClose={() => setConfirmation(null)}
        onConfirm={handleConfirmAction}
        title={confirmation?.type === 'delete_original' ? t('dashboard.confirmDeleteOriginalTitle') : t('dashboard.confirmDeleteTranscriptionTitle')}
        message={confirmation?.type === 'delete_original' 
          ? t('dashboard.confirmDeleteOriginalMessage')
          : t('dashboard.confirmDeleteTranscriptionMessage')
        }
        confirmText={t('common.delete')}
        variant="danger"
        isLoading={!!actionLoading}
      />

      <Toast message={toastMessage} isVisible={showToast} />
    </div>
  );
};

const Dashboard: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tab = searchParams.get('tab');

  return (
    <>
      {tab === 'history' || tab === 'files' ? (
        <History showFolders={false} enableBulkMove={false} enableDragMove={false} />
      ) : (
        <DashboardOverview />
      )}
    </>
  );
};

export default Dashboard;
